import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { SwitchComponent } from '../../../shared/components/form/input/switch.component';
import { ContactoApiService } from '../services/contacto-api.service';
import {
  Contact,
  ContactRequest,
  ContactState,
  ContactCycle,
  ContactOrigin,
  ContactAttributeDefinition,
  ContactAttributeCustomRequest,
  AttributeFormEntry
} from '../models/contacto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ContactCompanyResponse, CompanyResponse, CompanySummary } from '../../empresas/models/empresa.model';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { LeadDrawerService } from '../../leads/services/lead-drawer.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PhoneInputFlagComponent } from '../../../shared/components/form/phone-input-flag/phone-input-flag.component';
import { COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';
import { PermissionService } from '../../../shared/services/permission.service';

// Internal type for tracking companies in the form (pending or loaded)
interface PendingCompany {
  company: CompanySummary;
  position: string;
  isPrimary: boolean;
  associationId?: number; // Set when loaded from API in edit mode
}

// Resumen agregado de las sub-operaciones de un guardado compuesto
// (atributos personalizados / asociaciones de empresa)
interface SubOpsResult {
  failed: number;    // cuántas operaciones fallaron
  forbidden: boolean; // al menos un fallo fue 403 (sin permisos)
}

@Component({
  selector: 'app-contacto-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    SwitchComponent,
    PhoneInputFlagComponent,
    EntitySelectComponent
  ],
  templateUrl: './contacto-form.component.html'
})
export class ContactoFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  contactoId: number | null = null;

  currentUser: any = null;

  /**
   * Texto del responsable ya elegido, para que el buscador lo muestre al abrir en edición
   * (el control solo guarda el id — ver skill crm-entity-autocomplete, gotcha 2).
   *
   * inc38 — antes esto se rellenaba con `userRegisterId` y, si no se podía resolver, con el
   * usuario logeado. Resultado: la vista Edición decía "Admin AD" en un contacto que no
   * tenía responsable y la vista detalle decía "Sin información". La edición mentía; ahora
   * ambas leen el mismo campo (`userPropietyId`) y vacío se muestra vacío.
   */
  ownerDisplayName = '';

  /** Catálogo de países (bandera + código telefónico) para el prefijo del teléfono. */
  readonly phoneCountries = COUNTRIES_DATA;

  // Listas para selects (catálogos)
  contactStates: ContactState[] = [];
  contactCycles: ContactCycle[] = [];
  contactOrigins: ContactOrigin[] = [];
  loadingCatalogs = false;

  // Atributos personalizados (EAV)
  attributeEntries: AttributeFormEntry[] = [];
  loadingAttributes = false;

  // Ciclo original (para detectar cambio en edición)
  private originalCycleId: number | null = null;

  // Empresas Asociadas
  asociatedCompanies: PendingCompany[] = [];
  associationsToDelete: number[] = [];
  /** associationId → huella con la que se cargó, para detectar cambios reales (inc36). */
  private originalCompanies = new Map<number, string>();

  // ── Drawer mode support ──────────────────────────────────────────
  @Input() drawerMode = false;
  @Input() set prefilledCompanyId(id: number | undefined) { this._prefilledCompanyId = id; }
  @Input() set prefilledCompanyName(name: string | undefined) { this._prefilledCompanyName = name; }

  @Output() saved = new EventEmitter<Contact>();
  @Output() cancelled = new EventEmitter<void>();

  private _prefilledCompanyId?: number;
  private _prefilledCompanyName?: string;

  // Company search / typeahead
  companySearchTerm = '';
  companySearchResults: CompanyResponse[] = [];
  searchingCompanies = false;
  showCompanyDropdown = false;
  selectedCompanyToAdd: CompanyResponse | null = null;
  newCompanyPosition = '';
  newCompanyIsPrimary = false;

  private companySearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Trigger drawer state (CREATE_DEAL / CREATE_LEAD on cycle change in edit mode)
  private pendingTriggerType: 'CREATE_DEAL' | 'CREATE_LEAD' | null = null;
  private triggerResolved = false;

  constructor(
    private fb: FormBuilder,
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private empresaApi: EmpresaApiService,
    private router: Router,
    private route: ActivatedRoute,
    private dealDrawerService: DealDrawerService,
    private leadDrawerService: LeadDrawerService,
    private toast: ToastService,
    private permissions: PermissionService
  ) {
    this.initForm();
  }

  /**
   * inc39 — reasignar un contacto es una decisión de cartera, no un dato más del formulario.
   * Se exige el mismo permiso que el backend pide en PATCH /api/contacts/{id}/change-owner y
   * en la acción masiva CHANGE_OWNER: editar sobre el módulo 'contacts'. Quien no lo tenga
   * sigue viendo el responsable, pero en solo lectura.
   */
  get puedeEditarResponsable(): boolean {
    return this.permissions.has('contacts', 'edit');
  }

  // ── Buscador de responsable ──
  // Arrow functions a propósito: EntitySelectComponent las invoca como `this.searchFn(...)`
  // y un método normal perdería el `this` de este componente (skill, gotcha 1).
  // El filtrado es client-side porque el endpoint de usuarios no busca por texto (gotcha 3).
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.contactoApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.trim().toLowerCase();
        const items = t
          ? users.filter(u => `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t))
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string => `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  /**
   * Sin botón de limpiar (`allowClear=false`): el responsable se REASIGNA, no se vacía. El
   * backend ignora `userPropietyId` nulo en el PUT para no perder el dueño en actualizaciones
   * parciales, así que ofrecer una "X" prometería un borrado que nunca ocurre.
   */
  onOwnerSelected(user: CrmUserResponse | null): void {
    this.ownerDisplayName = user ? this.userLabel(user) : '';
  }

  ngOnInit(): void {

    this.currentUser = this.authService.getCurrentUser();
    this.aplicarResponsablePorDefecto();
    // Cargar catálogos
    this.cargarCatalogos();

    // Cargar definiciones de atributos personalizados
    this.cargarAtributosPersonalizados();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.contactoId = +id;
      this.cargarContacto(this.contactoId);
      this.cargarEmpresasAsociadas(this.contactoId);
    }

    // Cuando el drawer guarda el deal/lead disparado por el trigger, reanudar el guardado del contacto
    this.dealDrawerService.dealSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.pendingTriggerType === 'CREATE_DEAL') {
        this.pendingTriggerType = null;
        this.triggerResolved = true;
        this.guardar();
      }
    });
    this.leadDrawerService.leadSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.pendingTriggerType === 'CREATE_LEAD') {
        this.pendingTriggerType = null;
        this.triggerResolved = true;
        this.guardar();
      }
    });

    this.companySearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => {
        if (term.trim().length >= 2) {
          this.ejecutarBusquedaEmpresas(term);
        } else {
          this.companySearchResults = [];
          this.showCompanyDropdown = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarCatalogos(): void {
    this.loadingCatalogs = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Cargar todos los catálogos en paralelo
    Promise.all([
      this.contactoApi.getContactStates(businessId).toPromise(),
      this.contactoApi.getContactCycles(businessId).toPromise(),
      this.contactoApi.getContactOrigins(businessId).toPromise()
    ]).then(([states, cycles, origins]) => {
      this.contactStates = states || [];
      this.contactCycles = cycles || [];
      this.contactOrigins = origins || [];
      this.loadingCatalogs = false;
      if (this.drawerMode && !this.isEditing) {
        this.applyPrefill();
      }
    }).catch(() => {
      this.loadingCatalogs = false;
      this.error = 'Error al cargar catálogos';
    });
  }

  private cargarAtributosPersonalizados(): void {
    this.loadingAttributes = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactoApi.getAttributeDefinitions(businessId).subscribe({
      next: (definitions: ContactAttributeDefinition[]) => {
        this.attributeEntries = definitions
          .filter(def => def.isActive)
          .map(def => {
            let listOptionsParsed: string[] = [];
            if (def.listOptions) {
              try { listOptionsParsed = JSON.parse(def.listOptions); } catch { listOptionsParsed = []; }
            }
            return {
              definition: def,
              strValue: '',
              intValue: null,
              bolValue: false,
              strListValue: '',
              listOptionsParsed
            };
          });

        // Si estamos editando, cargar los valores existentes del contacto
        if (this.isEditing && this.contactoId) {
          this.cargarValoresAtributos(this.contactoId, businessId);
        } else {
          this.loadingAttributes = false;
        }
      },
      error: () => { this.loadingAttributes = false; }
    });
  }

  private cargarValoresAtributos(contactId: number, businessId: number): void {
    this.contactoApi.getContactCustomAttributes(contactId, businessId).subscribe({
      next: (values) => {
        values.forEach(val => {
          const entry = this.attributeEntries.find(e => e.definition.id === val.idAttributeDefinition);
          if (entry) {
            entry.existingId = val.id;
            entry.strValue = val.strValue || '';
            entry.intValue = val.intValue ?? null;
            entry.bolValue = val.bolValue ?? false;
            entry.strListValue = val.strListValue || '';
          }
        });
        this.loadingAttributes = false;
      },
      error: () => { this.loadingAttributes = false; }
    });
  }

  /**
   * Convierte los resultados de sub-operaciones (null/undefined = OK,
   * HttpErrorResponse = fallo) en un resumen. Un fallo no aborta al resto:
   * cada promesa ya viene con su propio catch.
   */
  private resumirFallos(operacion: string, results: unknown[]): SubOpsResult {
    const errors = results.filter((r): r is { status?: number } => r !== null && r !== undefined);
    if (errors.length > 0) {
      console.warn(`[ContactoForm] ${errors.length} operación(es) de ${operacion} fallaron al guardar:`, errors);
    }
    return {
      failed: errors.length,
      forbidden: errors.some(e => e?.status === 403)
    };
  }

  private guardarAtributosPersonalizados(contactId: number, businessId: number): Promise<SubOpsResult> {
    const promises: Promise<unknown>[] = this.attributeEntries.map(entry => {
      const req: ContactAttributeCustomRequest = {
        idAttributeDefinition: entry.definition.id,
        idContact: contactId,
        strValue: entry.definition.type === 'text' ? (entry.strValue || null) : null,
        intValue: entry.definition.type === 'number' ? entry.intValue : null,
        bolValue: entry.definition.type === 'boolean' ? entry.bolValue : null,
        strListValue: entry.definition.type === 'list' ? (entry.strListValue || null) : null,
        businessId
      };

      const hasValue = req.strValue !== null || req.intValue !== null ||
                       req.bolValue !== null || req.strListValue !== null;

      if (!hasValue && !entry.existingId) {
        // No hay valor y no existe en backend — no enviar
        return Promise.resolve(null);
      }

      if (!hasValue && entry.existingId) {
        // Tenía valor pero fue borrado — eliminar del backend
        return this.contactoApi.deleteCustomAttribute(entry.existingId).toPromise().then(() => null).catch(err => err);
      }

      if (entry.existingId) {
        // Actualizar valor existente
        return this.contactoApi.updateCustomAttribute(entry.existingId, req).toPromise().then(() => null).catch(err => err);
      } else {
        // Crear nuevo valor
        return this.contactoApi.createCustomAttribute(req).toPromise().then(() => null).catch(err => err);
      }
    });

    return Promise.all(promises).then(results => this.resumirFallos('atributos personalizados', results));
  }

  // Alineado con el backend: letras (incl. tildes/ñ), espacios, apóstrofes y guiones
  private static readonly NAME_PATTERN = /^[A-Za-zÀ-ÿÑñ' \-]*$/;
  // Alineado con el backend: dígitos, espacios, +, -, (), #
  private static readonly PHONE_PATTERN = /^[0-9+()\-\s#]*$/;

  private initForm(): void {
    // maxlengths alineados con la BD del backend: fullname/lastname 100, mail 150, phone 50, position 100
    this.form = this.fb.group({
      fullname: [null, [Validators.required, Validators.maxLength(100), Validators.pattern(ContactoFormComponent.NAME_PATTERN)]],
      // Apellido y correo NO son obligatorios: un contacto puede llegar por WhatsApp, donde lo
      // unico que sabemos de el es su telefono. Exigirlos dejaba esas fichas imposibles de guardar
      // (guardar() aborta si el form es invalido), asi que no habia forma de ponerles ni el nombre.
      // La BD ya los tiene nullable; el formato del correo se sigue validando cuando viene.
      lastname: [null, [Validators.maxLength(100), Validators.pattern(ContactoFormComponent.NAME_PATTERN)]],
      mail: [null, [Validators.email, Validators.maxLength(150)]],
      phone: [null, [Validators.maxLength(50), Validators.pattern(ContactoFormComponent.PHONE_PATTERN)]],
      position: [null, Validators.maxLength(100)],
      idContactState: [null],
      idContactCycle: [null, Validators.required],
      idOrigin: [null],
      userPropietyId: [null],
      score: [{ value: 0, disabled: true }, Validators.required]
    });
  }

  cargarContacto(id: number): void {
    this.loading = true;

    this.contactoApi.getContactById(id).subscribe({
      next: (contact: Contact) => {
        this.form.patchValue({
          fullname: contact.fullname,
          lastname: contact.lastname,
          mail: contact.mail,
          phone: contact.phone,
          position: contact.position,
          idContactState: contact.idContactState,
          idContactCycle: contact.idContactCycle,
          idOrigin: contact.idOrigin,
          userPropietyId: contact.userPropietyId ?? null,
          score: contact.score ?? 0
        });

        this.form.get('score')?.enable();

        this.originalCycleId = contact.idContactCycle ?? null;
        this.cargarNombreResponsable(contact.userPropietyId);

        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el contacto';
        this.loading = false;
      }
    });
  }

  /**
   * Al crear, el responsable por defecto es quien está creando el contacto — que es lo que
   * el backend asigna igualmente si el POST no manda `userPropietyId`. En edición lo
   * reemplaza `cargarContacto()`.
   */
  private aplicarResponsablePorDefecto(): void {
    if (!this.currentUser) return;
    this.form.patchValue({ userPropietyId: this.currentUser.id });
    this.ownerDisplayName = `${this.currentUser.fullname} ${this.currentUser.lastname || ''}`.trim();
  }

  /**
   * Resuelve el nombre del responsable guardado para pintarlo en el buscador. Un contacto
   * sin responsable (creado por un formulario antiguo, importado…) deja el campo vacío: el
   * placeholder ya invita a asignar uno, y rellenarlo con el usuario logeado era justo la
   * mentira del inc38.
   */
  private cargarNombreResponsable(ownerId?: number): void {
    if (!ownerId) {
      this.ownerDisplayName = '';
      return;
    }
    if (ownerId === this.currentUser?.id) {
      this.ownerDisplayName = `${this.currentUser.fullname} ${this.currentUser.lastname || ''}`.trim();
      return;
    }
    this.authApiService.getUserById(ownerId).subscribe({
      next: (u) => { this.ownerDisplayName = `${u.fullname} ${u.lastname || ''}`.trim(); },
      // El directorio resuelve también a los inactivos: fallar aquí es «ya no existe».
      error: () => { this.ownerDisplayName = 'Usuario no disponible'; }
    });
  }

  private cargarEmpresasAsociadas(idContact: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactCompanyApi.getByContact(idContact, businessId).subscribe({
      next: (associations: ContactCompanyResponse[]) => {
        this.asociatedCompanies = associations.map(assoc => ({
          company: assoc.company
            ? assoc.company
            : { id: assoc.idCompany, commercialName: `Empresa #${assoc.idCompany}` },
          position: assoc.position || '',
          isPrimary: assoc.isPrimary,
          associationId: assoc.id
        }));
        // inc36 — foto de cómo llegaron, para no reenviar al guardar las que nadie tocó.
        this.originalCompanies = new Map(
          this.asociatedCompanies
            .filter(pc => pc.associationId)
            .map(pc => [pc.associationId!, this.huellaEmpresa(pc)])
        );
      },
      error: () => { /* Non-fatal - show empty list */ }
    });
  }

  /** Huella comparable de una asociación: los tres campos que el usuario puede cambiar. */
  private huellaEmpresa(pc: PendingCompany): string {
    return `${pc.company.id}|${pc.isPrimary ? 1 : 0}|${(pc.position || '').trim()}`;
  }

  // --- Company search typeahead ---

  onCompanySearchInput(value: string): void {
    this.companySearchTerm = value;
    this.selectedCompanyToAdd = null;
    this.companySearch$.next(value);
  }

  private ejecutarBusquedaEmpresas(term: string): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.searchingCompanies = true;
    this.empresaApi.searchCompanies({ businessId, searchTerm: term, page: 0, size: 10 }).subscribe({
      next: (page) => {
        const addedIds = this.asociatedCompanies.map(pc => pc.company.id);
        this.companySearchResults = page.content.filter(c => !addedIds.includes(c.id));
        this.showCompanyDropdown = this.companySearchResults.length > 0;
        this.searchingCompanies = false;
      },
      error: () => {
        this.searchingCompanies = false;
        this.companySearchResults = [];
        this.showCompanyDropdown = false;
      }
    });
  }

  selectCompany(company: CompanyResponse): void {
    this.selectedCompanyToAdd = company;
    this.companySearchTerm = company.commercialName;
    this.showCompanyDropdown = false;
    this.companySearchResults = [];
  }

  agregarEmpresa(): void {
    if (!this.selectedCompanyToAdd) return;

    const newEntry: PendingCompany = {
      company: {
        id: this.selectedCompanyToAdd.id,
        commercialName: this.selectedCompanyToAdd.commercialName,
        phone: this.selectedCompanyToAdd.phone,
        website: this.selectedCompanyToAdd.website
      },
      position: this.newCompanyPosition.trim(),
      isPrimary: this.newCompanyIsPrimary
    };

    if (newEntry.isPrimary) {
      this.asociatedCompanies.forEach(pc => pc.isPrimary = false);
    }

    this.asociatedCompanies.push(newEntry);

    // Reset fields
    this.selectedCompanyToAdd = null;
    this.companySearchTerm = '';
    this.newCompanyPosition = '';
    this.newCompanyIsPrimary = false;
    this.companySearchResults = [];
    this.showCompanyDropdown = false;
  }

  quitarEmpresa(index: number): void {
    const entry = this.asociatedCompanies[index];
    if (entry.associationId) {
      this.associationsToDelete.push(entry.associationId);
    }
    this.asociatedCompanies.splice(index, 1);
  }

  togglePrimaryEmpresa(index: number): void {
    const wasPrimary = this.asociatedCompanies[index].isPrimary;
    this.asociatedCompanies.forEach(pc => pc.isPrimary = false);
    this.asociatedCompanies[index].isPrimary = !wasPrimary;
  }

  getCompanyInitials(company: CompanySummary): string {
    const name = company.commercialName || '';
    return name.substring(0, 2).toUpperCase();
  }

  getCompanyAvatarColor(company: CompanySummary): string {
    const colors = [
      'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    ];
    const name = company.commercialName || '';
    const charSum = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  }

  hideCompanyDropdownDelayed(): void {
    setTimeout(() => { this.showCompanyDropdown = false; }, 200);
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Intercept CREATE_DEAL / CREATE_LEAD trigger on cycle change (edit mode)
    if (this.isEditing && this.contactoId && !this.triggerResolved) {
      const newCycleId = this.form.value.idContactCycle;
      const cycleChanged = (newCycleId ?? null) !== this.originalCycleId;
      if (cycleChanged && newCycleId) {
        const cycle = this.contactCycles.find(c => c.id === newCycleId);
        if (cycle?.triggerAction === 'CREATE_DEAL' || cycle?.triggerAction === 'CREATE_LEAD') {
          const contactName = `${this.form.value.fullname || ''} ${this.form.value.lastname || ''}`.trim();
          const primary = this.asociatedCompanies.find(c => c.isPrimary) || this.asociatedCompanies[0];
          const opts = {
            contactId: this.contactoId,
            contactName,
            companyId: primary?.company?.id,
            companyName: primary?.company?.commercialName
          };
          this.pendingTriggerType = cycle.triggerAction;
          if (cycle.triggerAction === 'CREATE_DEAL') {
            this.dealDrawerService.open(opts);
          } else {
            this.leadDrawerService.open(opts);
          }
          return;
        }
      }
    }
    this.triggerResolved = false;

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Sin permiso para reasignar, el responsable ni siquiera viaja en el body: el backend
    // ignora userPropietyId nulo y conserva el dueño actual (inc39).
    const formValue = { ...this.form.value };
    if (!this.puedeEditarResponsable) {
      delete formValue.userPropietyId;
    }

    if (this.isEditing && this.contactoId) {
      // PUT: no enviar idContactCycle — se cambia solo via change-cycle
      const { idContactCycle: newCycleId, ...restFormValues } = formValue;
      const contactRequest: ContactRequest = { ...restFormValues, businessId };

      this.contactoApi.updateContact(this.contactoId, contactRequest).subscribe({
        next: () => {
          const contactId = this.contactoId!;
          const cycleChanged = (newCycleId ?? null) !== this.originalCycleId;
          const savedContact = { id: contactId, ...restFormValues, businessId } as Contact;

          const afterCycleChange = () => {
            this.guardarAtributosPersonalizados(contactId, businessId).then(attrResult =>
              this.guardarAsociacionesEmpresas(contactId, businessId, true).then(companiesResult =>
                this.finalizarGuardado('actualizó', attrResult, companiesResult, savedContact)
              )
            );
          };

          if (cycleChanged && newCycleId) {
            this.contactoApi.changeContactCycle(contactId, newCycleId).subscribe({
              next: () => afterCycleChange(),
              error: () => afterCycleChange()
            });
          } else {
            afterCycleChange();
          }
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el contacto';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      // POST (crear): idContactCycle sí va en el body
      const contactRequest: ContactRequest = { ...formValue, businessId };
      this.contactoApi.createContact(contactRequest).subscribe({
        next: (createdContact: Contact) => {
          this.guardarAtributosPersonalizados(createdContact.id!, businessId).then(attrResult =>
            this.guardarAsociacionesEmpresas(createdContact.id!, businessId, false).then(companiesResult =>
              this.finalizarGuardado('creó', attrResult, companiesResult, createdContact)
            )
          );
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al registrar el contacto';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  private guardarAsociacionesEmpresas(idContact: number, businessId: number, isEdit: boolean): Promise<SubOpsResult> {
    const deletePromises: Promise<unknown>[] = this.associationsToDelete.map(assocId =>
      this.contactCompanyApi.deleteAssociation(assocId, businessId).toPromise().then(() => null).catch(err => err)
    );

    // New companies to create (no associationId yet)
    const toCreate = isEdit
      ? this.asociatedCompanies.filter(pc => !pc.associationId)
      : this.asociatedCompanies;

    const createPromises: Promise<unknown>[] = toCreate
      .filter(pc => pc.company.id != null)
      .map(pc =>
        this.contactCompanyApi.createAssociation({
          idContact,
          idCompany: pc.company.id,
          businessId,
          isPrimary: pc.isPrimary,
          position: pc.position || undefined
        }).toPromise().then(() => null).catch(err => err)
      );

    // Update existing associations (edit mode only).
    // inc36 — solo las que cambiaron. Antes se reenviaba TODA asociación existente en cada
    // guardado del contacto; el backend las reescribía y auditaba, y como este bloque corre
    // DESPUÉS del PUT del contacto, la línea de tiempo terminaba encabezada por un
    // "Empresa asociada" que el usuario nunca tocó.
    const updatePromises: Promise<unknown>[] = isEdit
      ? this.asociatedCompanies
          .filter(pc => pc.associationId && pc.company.id != null)
          .filter(pc => this.originalCompanies.get(pc.associationId!) !== this.huellaEmpresa(pc))
          .map(pc =>
            this.contactCompanyApi.updateAssociation(pc.associationId!, {
              idContact,
              idCompany: pc.company.id,
              businessId,
              isPrimary: pc.isPrimary,
              position: pc.position || undefined
            }, businessId).toPromise().then(() => null).catch(err => err)
          )
      : [];

    return Promise.all([...deletePromises, ...createPromises, ...updatePromises])
      .then(results => this.resumirFallos('empresas vinculadas', results));
  }

  /**
   * Feedback consolidado del guardado compuesto. El contacto base ya se guardó
   * en este punto: el form se cierra / navega igual, pero el toast distingue
   * éxito total de guardado parcial (fallaron atributos y/o empresas).
   */
  private finalizarGuardado(
    accion: 'creó' | 'actualizó',
    attrResult: SubOpsResult,
    companiesResult: SubOpsResult,
    savedContact?: Contact
  ): void {
    this.saving = false;

    const partesFallidas: string[] = [];
    if (companiesResult.failed > 0) partesFallidas.push('las empresas vinculadas');
    if (attrResult.failed > 0) partesFallidas.push('los atributos personalizados');

    if (partesFallidas.length === 0) {
      if (accion === 'creó') {
        this.toast.created('Contacto');
      } else {
        this.toast.updated('Contacto');
      }
    } else {
      let message = `El contacto se ${accion}, pero no se pudieron guardar: ${partesFallidas.join(' y ')}.`;
      if (attrResult.forbidden || companiesResult.forbidden) {
        message += ' No tienes permisos para modificarlos.';
      }
      this.toast.warning({ title: 'Guardado parcial', message, duration: 8000 });
    }

    if (this.drawerMode) {
      this.saved.emit(savedContact);
    } else {
      this.router.navigate(['/contactos']);
    }
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/contactos']);
    }
  }

  /** Called by drawer before opening */
  reset(): void {
    this.isEditing = false;
    this.contactoId = null;
    this.originalCycleId = null;
    this.asociatedCompanies = [];
    this.associationsToDelete = [];
    this.originalCompanies.clear();
    this.companySearchTerm = '';
    this.selectedCompanyToAdd = null;
    this.newCompanyPosition = '';
    this.newCompanyIsPrimary = false;
    this.companySearchResults = [];
    this.showCompanyDropdown = false;
    this.attributeEntries = [];
    this.error = '';
    this.saving = false;
    this.initForm();
    this.aplicarResponsablePorDefecto();
    this.cargarAtributosPersonalizados();
  }

  /** Called by drawer in edit mode */
  loadContacto(id: number): void {
    this.isEditing = true;
    this.contactoId = id;
    this.cargarContacto(id);
    this.cargarEmpresasAsociadas(id);
  }

  private applyPrefill(): void {
    if (this._prefilledCompanyId) {
      this.asociatedCompanies = [{
        company: {
          id: this._prefilledCompanyId,
          commercialName: this._prefilledCompanyName || `Empresa #${this._prefilledCompanyId}`
        },
        position: '',
        isPrimary: true
      }];
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['email']) return 'Email inválido';
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['pattern']) {
      if (field === 'fullname' || field === 'lastname') return 'Solo letras, espacios, apóstrofes y guiones';
      if (field === 'phone') return 'Solo números y símbolos válidos de teléfono';
      return 'Formato inválido';
    }

    return 'Campo inválido';
  }
}
